import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  Consultant, 
  DateData, 
  Slot, 
  Pricing, 
  Appointment, 
  ApiResponse, 
  GetAppointmentsQuery 
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL:'https://api.siis.ca',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/user/login', credentials);
    return response.data;
  }

  // Admin panel endpoints
  async getConsultants(): Promise<ApiResponse<Consultant[]>> {
    const response: AxiosResponse<ApiResponse<Consultant[]>> = await this.api.get('/admin-panel/get-consultants');
    return response.data;
  }

  async getDates(): Promise<ApiResponse<DateData[]>> {
    const response: AxiosResponse<ApiResponse<DateData[]>> = await this.api.get('/admin-panel/get-dates');
    return response.data;
  }

  async getSlots(dateId: string): Promise<ApiResponse<Slot[]>> {
    const response: AxiosResponse<ApiResponse<Slot[]>> = await this.api.get(`/admin-panel/get-slots/${dateId}`);
    return response.data;
  }

  async getPricing(): Promise<ApiResponse<Pricing[]>> {
    const response: AxiosResponse<ApiResponse<Pricing[]>> = await this.api.get('/admin-panel/get-pricing');
    return response.data;
  }

  async getAppointments(query: GetAppointmentsQuery = {}): Promise<ApiResponse<Appointment[]>> {
    const response: AxiosResponse<ApiResponse<Appointment[]>> = await this.api.get('/admin-panel/get-appointments', {
      params: query,
    });
    return response.data;
  }

  async getAppointment(appointmentId: string): Promise<ApiResponse<Appointment>> {
    const response: AxiosResponse<ApiResponse<Appointment>> = await this.api.get(`/admin-panel/get-appointment/${appointmentId}`);
    return response.data;
  }

  // Date management
  async activateDate(dateId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/activate-date/${dateId}`);
    return response.data;
  }

  async inactivateDate(dateId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/inavtivate-date/${dateId}`);
    return response.data;
  }

  // Slot management
  async activateSlot(slotId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/activate-slot/${slotId}`);
    return response.data;
  }

  async inactivateSlot(slotId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/inavtivate-slot/${slotId}`);
    return response.data;
  }

  // Slot consultant mapping management
  async activateSlotConsultantMapping(mappingId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/activate-slot-consultant-mapping/${mappingId}`);
    return response.data;
  }

  async inactivateSlotConsultantMapping(mappingId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/inavtivate-slot-consultant-mapping/${mappingId}`);
    return response.data;
  }

  // Pricing management
  async updatePrice(priceId: string, price: number): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.put(`/admin-panel/update-price/${priceId}`, { price });
    return response.data;
  }

  // Appointment management
  async cancelAppointment(appointmentId: string): Promise<ApiResponse<void>> {
    const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/admin-panel/cancel-appointment/${appointmentId}`);
    return response.data;
  }

  // Custom payment management
  async getCustomPayments(query: GetAppointmentsQuery = {}): Promise<ApiResponse<any[]>> {
    const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin-panel/get-custom-payments', {
      params: query,
    });
    return response.data;
  }

  async getCustomPayment(customPaymentId: string): Promise<ApiResponse<any>> {
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin-panel/get-custom-payment/${customPaymentId}`);
    return response.data;
  }
}

export const apiService = new ApiService();
